/**
 * Inline Formatting Input Rules Plugin for ProseMirror
 *
 * Provides markdown-style shortcuts for inline formatting:
 * - "**text**" or "__text__" → bold (strong mark)
 * - "*text*" or "_text_" → italic (em mark)
 * - "`text`" → inline code (code mark)
 *
 * Features:
 * - Only activates when editor is editable
 * - Applies marks and removes delimiter text
 * - Prevents mark from continuing after closing delimiter
 * - Works with keyboard shortcut toggle (Cmd+B, Cmd+I)
 */

import type { Plugin } from "@tiptap/pm/state";
import { PluginKey } from "@tiptap/pm/state";
import type { MarkType } from "@tiptap/pm/model";
import { InputRule, inputRules } from "@tiptap/pm/inputrules";
import { schema } from "../schema";

export const inlineFormattingInputRulesPluginKey = new PluginKey(
  "inlineFormattingInputRules"
);

/**
 * Creates a custom InputRule for applying marks (bold, italic, code).
 *
 * This is a custom implementation since @tiptap/core's markInputRule
 * is not available in the base @tiptap/pm packages.
 *
 * Based on ProseMirror community patterns:
 * https://dragonman225.js.org/prosemirror-inputrules-md-regex.html
 * https://discuss.prosemirror.net/t/input-rules-for-wrapping-marks/537
 *
 * @param regexp - Regular expression with capture group for the text to mark
 * @param markType - The mark type to apply (e.g., schema.marks.strong)
 * @returns InputRule that applies the mark when pattern matches
 */
function markInputRule(regexp: RegExp, markType: MarkType): InputRule {
  return new InputRule(regexp, (state, match, start, end) => {
    const tr = state.tr;

    // match[1] contains the captured text between delimiters
    if (match[1]) {
      const textStart = start + match[0].indexOf(match[1]);
      const textEnd = textStart + match[1].length;

      // Remove closing delimiter
      if (textEnd < end) {
        tr.delete(textEnd, end);
      }

      // Remove opening delimiter
      if (textStart > start) {
        tr.delete(start, textStart);
      }

      // Adjust end position after deletions
      const finalEnd = start + match[1].length;

      // Apply the mark to the captured text
      tr.addMark(start, finalEnd, markType.create());

      // CRITICAL: Remove stored mark to prevent it from applying to
      // subsequent text typed after the closing delimiter
      tr.removeStoredMark(markType);
    }

    return tr;
  });
}

/**
 * Creates a plugin that enables markdown-style inline formatting shortcuts.
 *
 * Typing "**text**" or "__text__" makes text bold.
 * Typing "*text*" or "_text_" makes text italic.
 * Typing "`text`" makes text inline code.
 *
 * The delimiters are removed from the document after the mark is applied.
 *
 * @returns Plugin that handles inline formatting input rules
 */
export function createInlineFormattingInputRulesPlugin(): Plugin {
  // Bold: "**text**" or "__text__"
  // Pattern: (?:asterisk-asterisk|__)([^asterisk_]+)(?:asterisk-asterisk|__)$
  // - (?:asterisk-asterisk|__) = Match ** or __ (non-capturing group)
  // - ([^asterisk_]+) = Capture one or more chars that aren't * or _
  // - (?:asterisk-asterisk|__)$ = Match closing ** or __ at cursor position
  //
  // Character class [^asterisk_]+ prevents nested delimiters like **text**more**
  // The $ anchor ensures match only happens when user types closing delimiter
  const boldRule = markInputRule(
    /(?:\*\*|__)([^*_]+)(?:\*\*|__)$/,
    schema.marks.strong
  );

  // Italic: "*text*" or "_text_"
  // Pattern: (?:^|[^asterisk_])(?:asterisk|_)([^asterisk_]+)(?:asterisk|_)$
  // - (?:^|[^asterisk_]) = Guard: start of line or non-delimiter (prevents **)
  // - (?:asterisk|_) = Match single * or _
  // - ([^asterisk_]+) = Capture one or more non-delimiter chars
  // - (?:asterisk|_)$ = Match closing * or _ at cursor position
  //
  // Guard pattern prevents false positive when typing **text**
  // (prevents the * in ** from being interpreted as italic start)
  const italicRule = markInputRule(
    /(?:^|[^*_])(?:\*|_)([^*_]+)(?:\*|_)$/,
    schema.marks.em
  );

  // Inline Code: "`text`"
  // Pattern: `([^`]+)`$
  // - ` = Opening backtick
  // - ([^`]+) = Capture one or more non-backtick chars
  // - `$ = Closing backtick at cursor position
  //
  // Simple pattern since backticks don't have ambiguity like * and _
  const codeRule = markInputRule(/`([^`]+)`$/, schema.marks.code);

  // Order matters: more specific patterns first
  // Bold rule checks for ** or __ (more specific)
  // Italic rule checks for * or _ (less specific)
  // Code rule is independent
  return inputRules({
    rules: [boldRule, italicRule, codeRule],
  });
}
